import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from 'helpers/api_helper';

// Types
export interface ReferredUser {
  id: number;
  agent: number;
  agent_name: string;
  subagent: number | null;
  subagent_name: string | null;
  user: number;
  user_name: string;
  created_at: string;
}

export interface ReferredUsersResponse {
  success: boolean;
  count: number;
  page: number;
  page_size: number;
  results: ReferredUser[];
}

export interface ReferredUsersState {
  data: ReferredUser[];
  loading: boolean;
  error: string | null;
  count: number;
  page: number;
  page_size: number;
  hasMore: boolean;
}

const initialState: ReferredUsersState = {
  data: [],
  loading: false,
  error: null,
  count: 0,
  page: 1,
  page_size: 25,
  hasMore: false,
};

// Async thunk for fetching referred users
export const fetchReferredUsers = createAsyncThunk(
  'referredUsers/fetchReferredUsers',
  async (params: { page?: number; page_size?: number } = {}) => {
    try {
      const response = await api.get<ReferredUsersResponse>('/agents/mapped-users/', {
        params: {
          page: params.page || 1,
          page_size: params.page_size || 25,
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch referred users');
    }
  }
);

const referredUsersSlice = createSlice({
  name: 'referredUsers',
  initialState,
  reducers: {
    clearReferredUsers: (state) => {
      state.data = [];
      state.count = 0;
      state.page = 1;
      state.hasMore = false;
      state.error = null;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReferredUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReferredUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        
        const { results, count, page, page_size } = action.payload;
        
        // If it's the first page, replace data, otherwise append
        if (page === 1) {
          state.data = results;
        } else {
          state.data = [...state.data, ...results];
        }
        
        state.count = count;
        state.page = page;
        state.page_size = page_size;
        state.hasMore = results.length === page_size && state.data.length < count;
      })
      .addCase(fetchReferredUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch referred users';
      });
  },
});

export const { clearReferredUsers, setPage } = referredUsersSlice.actions;
export default referredUsersSlice.reducer;
